import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CreditCard, ArrowLeft, Warning, SpinnerGap } from "@phosphor-icons/react";
import { myApi } from "../api";
import { gaEcommerce, cartToGaItems, getGaClientId } from "@/lib/ga";
import { useCart } from "../contexts/CartContext";
import { validateCheckout } from "../../shared/checkoutValidation";
import { trackCheckout, cartSnapshot } from "../lib/checkoutTracker";
import PaymentMethodSelector from "../components/shop/checkout/PaymentMethodSelector";
import PaymentResult from "../components/shop/checkout/PaymentResult";
import { useRecaptchaExecute } from "../../shared/recaptcha";

const CHECKOUT_KEY = "my_checkout_state";

// Nome do método de pagamento tal como aparece ao cliente (igual ao
// PaymentMethodSelector). Usado apenas no payment_type do order_placed.
const PAYMENT_LABELS = {
  MB: "Multibanco",
  MW: "MBWay",
  TB: "Transferência Bancária",
  PP: "PayPal",
  CT: "Cartão de Crédito",
  CP: "Cofidis Pay (até 12x)",
};

const fmt = (v, c = "EUR") =>
  new Intl.NumberFormat("pt-PT", { style: "currency", currency: c || "EUR" }).format(v || 0);

/** Normaliza o telemóvel MBWay para o formato exigido pelo gateway: "<prefixo> <numero>". */
import { formatMbwayPhone as _formatMbwayPhone, isValidMbwayPhone as _isValidMbwayPhone } from "../../shared/checkout/mbway";

/**
 * Final payment step: user picks the payment method and confirms the order.
 * On success we display method-specific instructions and clear the cart.
 * Relies on the state persisted by MyCheckout (sessionStorage:my_checkout_state).
 */
export default function MyCheckoutPayment() {
  const navigate = useNavigate();
  const { cart, itemCount, currency, produtos, packs, refresh: refreshCart } = useCart();
  const execRecaptcha = useRecaptchaExecute();

  const checkoutState = (() => {
    try { return JSON.parse(window.sessionStorage.getItem(CHECKOUT_KEY) || "{}"); } catch { return {}; }
  })();

  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState("");
  const [mbwayPhone, setMbwayPhone] = useState("");
  const [verifyData, setVerifyData] = useState(null);
  const [deliveryMethods, setDeliveryMethods] = useState([]);
  const [cartFlags, setCartFlags] = useState({ requiresShipping: true, defaultDeliveryMethodId: "" });
  // NIF da morada de facturação — usado para validar Cofidis Pay (proibido
  // para NIFs de empresa que começam por 5-9).
  const [billingNif, setBillingNif] = useState("");

  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState("");
  const [block, setBlock] = useState(null);
  // Só avaliamos o bloqueio de morada de envio DEPOIS de as flags do carrinho
  // terem carregado. Caso contrário, o estado inicial `requiresShipping: true`
  // (ou o fallback em caso de erro) provocava um "sem_morada_envio" transitório
  // para carrinhos só de cursos/eventos/digitais — que ficava registado no
  // funil de checkout como falso bloqueio.
  const [flagsLoaded, setFlagsLoaded] = useState(false);
  const [confirmResult, setConfirmResult] = useState(null);
  // Guard contra a race condition entre `refreshCart()` (que esvazia o cart
  // logo após criarmos a encomenda) e o useEffect abaixo que redirecciona
  // para `/my/carrinho` quando `itemCount === 0`. Sem esta flag, o utilizador
  // saltava para o carrinho vazio em vez da página de pagamento.
  const [navigatingToPayment, setNavigatingToPayment] = useState(false);

  // Use the verify data as the single source of truth for the final total,
  // since it reflects all applied discounts (promo + vouchers + credits +
  // portes). Fallback to the raw cart total only while verify is in-flight.
  const displayTotal = verifyData?.Total != null ? Number(verifyData.Total) : 0;
  // Amounts actually accepted by the server (prevents mismatch with the
  // input box when the user types more than the cap).
  const acceptedVouchers = verifyData?.vouchers != null ? Number(verifyData.vouchers) : 0;
  const acceptedCredits = verifyData?.credits != null ? Number(verifyData.credits) : 0;
  const acceptedPromoKey = verifyData?.ChaveCodigoPromocional || "";

  // Bloqueio explícito (em vez de redireccionar em silêncio): mostra ao
  // cliente PORQUÊ não pode avançar e um botão para corrigir. Clientes
  // reportavam "não consigo avançar e não percebo porquê" — este ecrã resolve.
  useEffect(() => {
    if (cart === null) return; // still loading
    if (navigatingToPayment) return; // a confirmar/redireccionar — ignorar
    if (itemCount === 0 && !confirmResult) {
      setBlock({
        msg: "O seu carrinho está vazio. Adicione produtos antes de finalizar a compra.",
        cta: "Ver produtos", to: "/my", reason: "carrinho_vazio",
      });
      return;
    }
    if (!checkoutState.billingAddressId) {
      setBlock({
        msg: "Falta indicar a morada de faturação. Volte aos dados de entrega para a selecionar ou criar.",
        cta: "Indicar morada de faturação", to: "/my/checkout", reason: "sem_morada_faturacao",
      });
      return;
    }
    if (flagsLoaded && cartFlags?.requiresShipping && !checkoutState.shippingAddressId) {
      setBlock({
        msg: "Falta indicar a morada de envio. Volte aos dados de entrega para a selecionar.",
        cta: "Indicar morada de envio", to: "/my/checkout", reason: "sem_morada_envio",
      });
      return;
    }
    setBlock(null);
    if (cart !== null && itemCount > 0) trackCheckout("payment", cartSnapshot(produtos, packs));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, itemCount, confirmResult, navigatingToPayment, flagsLoaded, cartFlags?.requiresShipping]);

  // Regista no funil o bloqueio que o cliente não conseguiu ultrapassar.
  useEffect(() => {
    if (block?.reason) trackCheckout("blocked", { reason: block.reason });
  }, [block?.reason]);

  // Load payment methods, delivery methods and verify order (to know flags)
  useEffect(() => {
    // Force-refresh do cart para mantêr o badge do menu e o expansor em sync.
    refreshCart?.();
    Promise.all([
      myApi.shopPaymentMethods().catch(() => []),
      myApi.shopDeliveryMethods().catch(() => []),
      myApi.shopCheckoutVerify({
        ID_TipoTransporte: checkoutState.deliveryMethod,
        ID_ClienteMoradaEnvio: checkoutState.shippingAddressId,
        PromotionalCode: checkoutState.promoCode || "",
        Vouchers: checkoutState.voucherAmount || 0,
        Credits: checkoutState.creditsAmount || 0,
        Observacoes: checkoutState.observations || "",
      }).catch(() => null),
      myApi.shopBillingAddresses().catch(() => []),
      myApi.shopCartFlags().catch(() => ({ requiresShipping: true })),
    ]).then(([pm, dm, ver, ba, cf]) => {
      const pmArr = Array.isArray(pm) ? pm : (pm?.items || pm?.obj || []);
      const dmArr = Array.isArray(dm) ? dm : (dm?.shipmentTypes || dm?.obj || dm?.items || []);
      setMethods(Array.isArray(pmArr) ? pmArr : []);
      setDeliveryMethods(Array.isArray(dmArr) ? dmArr : []);
      setVerifyData(ver?.checkoutData || ver?.obj || ver);
      setCartFlags({
        requiresShipping: !!cf?.requiresShipping,
        defaultDeliveryMethodId: cf?.defaultDeliveryMethodId || "",
      });
      // Find the selected billing address's NIF for Cofidis validation.
      const baArr = Array.isArray(ba) ? ba : (ba?.items || ba?.obj || []);
      const picked = baArr.find((a) =>
        String(a.ID_Cliente_Morada ?? a.ID_ClienteMorada) === String(checkoutState.billingAddressId));
      setBillingNif((picked?.Nif || picked?.NIF || "").toString().trim());
    }).finally(() => { setLoading(false); setFlagsLoaded(true); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirm = async () => {
    setConfirmError("");
    // Erro visível: scroll até ao bloco de pagamento/mensagem para o cliente
    // perceber logo o que falta (mesmo padrão do registo e do checkout).
    const falhar = (msg) => {
      setConfirmError(msg);
      trackCheckout("error", { error: msg });
      setTimeout(() => {
        const el = document.querySelector('[data-testid="payment-confirm-error"]')
          || document.querySelector('[data-testid="payment-methods-section"]');
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 60);
    };
    // Reaplicar TODAS as validações do legacy (consistente com MyCheckout)
    const v = validateCheckout({
      verifyData,
      billingAddressId: checkoutState.billingAddressId,
      shippingAddressId: checkoutState.shippingAddressId,
      deliveryMethod: checkoutState.deliveryMethod, deliveryMethods,
      paymentMethod: selected, paymentMethods: methods,
      mbwayPhone, cartEmpty: itemCount === 0,
      billingNif,
      requiresShippingOverride: cartFlags.requiresShipping,
    });
    if (!v.ok) { falhar(v.message); return; }

    // MBWay: validar formato (prefixo + número) ANTES de submeter ao backend.
    if (selected === "MW" && !_isValidMbwayPhone(mbwayPhone)) {
      falhar("Para MBWay indique o número de telemóvel com prefixo internacional (ex: 351 934751347).");
      return;
    }

    setConfirming(true);
    gaEcommerce("add_payment_info", cartToGaItems(produtos, packs), displayTotal, currency,
      { payment_type: selected });
    try {
      const recaptchaToken = await execRecaptcha("checkout");
      const r = await myApi.shopCheckoutConfirm({
        ID_TipoTransporte: checkoutState.deliveryMethod,
        ID_TipoPagamento: selected,
        ID_ClienteMoradaEnvio: checkoutState.shippingAddressId,
        ID_ClienteMoradaFatura: checkoutState.billingAddressId,
        // Use the amounts the server actually accepted (from last verify),
        // not the raw user input — otherwise the cap logic is bypassed.
        ChaveCodigoPromocional: acceptedPromoKey || checkoutState.promoCode || "",
        DescontoVale: acceptedVouchers,
        DescontoCreditos: acceptedCredits,
        ID_Portes: verifyData?.ID_Portes || "",
        Observacoes: checkoutState.observations || "",
        NumeroTelemovelPagamento: selected === "MW" ? _formatMbwayPhone(mbwayPhone) : "",
        Parcelas: 1,
        recaptcha_token: recaptchaToken,
        AnalyticsClientId: getGaClientId(),
      });
      if (r?.success === false) {
        falhar(r.message || "Não foi possível confirmar a encomenda.");
        return;
      }
      // order_placed (encomenda feita) — enviado UMA vez por encomenda, logo
      // após a criação com sucesso e imediatamente antes do que já acontece a
      // seguir (navegação/refresh). transaction_id = ID_Encomenda (o mesmo
      // valor que o servidor usa no purchase). Dentro de try/catch: nunca
      // atrasa, bloqueia ou altera o fluxo de pagamento.
      try {
        const txId = r?.ID_Encomenda ?? r?.obj?.ID_Encomenda;
        if (txId != null) {
          const sentKey = `ihtp_order_placed_sent:${txId}`;
          if (!window.sessionStorage.getItem(sentKey)) {
            const paymentLabel =
              PAYMENT_LABELS[selected]
              || methods.find((m) => m.ID_TipoPagamento === selected)?.Descricao
              || selected;
            gaEcommerce("order_placed", cartToGaItems(produtos, packs), displayTotal, currency,
              { transaction_id: String(txId), payment_type: paymentLabel });
            window.sessionStorage.setItem(sentKey, "1");
          }
        }
      } catch (_ga) { /* analytics nunca bloqueia o pagamento */ }
      // Hybrid backend now returns {ID_Encomenda, HashEncomenda, paymentData}.
      // Navegar PRIMEIRO para a página de pagamento (com a flag activada para
      // suprimir o useEffect que redirecciona para `/my/carrinho` quando o
      // cart é esvaziado pelo refreshCart). Só depois é que limpamos o
      // sessionStorage e fazemos refresh do cart (fire-and-forget).
      const hash = r?.HashEncomenda || r?.obj?.HashEncomenda;
      trackCheckout("completed", { order_id: r?.ID_Encomenda || r?.obj?.ID_Encomenda });
      if (hash) {
        setNavigatingToPayment(true);
        window.sessionStorage.removeItem(CHECKOUT_KEY);
        navigate(`/my/orders/${encodeURIComponent(hash)}/payment`, { replace: true });
        refreshCart().catch(() => {}); // fire-and-forget — não bloqueia a navegação
        return;
      }
      // Fallback: if hash missing for some reason, keep old inline result.
      window.sessionStorage.removeItem(CHECKOUT_KEY);
      await refreshCart();
      setConfirmResult(r?.obj || r);
    } catch (e) {
      falhar(e.message || "Erro ao confirmar encomenda.");
    } finally {
      setConfirming(false);
    }
  };

  if (confirmResult) {
    return (
      <div className="min-h-full bg-[#F9FAFB] py-8 px-4 sm:px-6 lg:px-8" style={{ fontFamily: "'Manrope', sans-serif" }} data-testid="my-checkout-payment-success">
        <div className="max-w-2xl mx-auto">
          <PaymentResult paymentMethod={selected} data={confirmResult} total={displayTotal} currency={currency} />
        </div>
      </div>
    );
  }

  if (block) {
    return (
      <div className="min-h-full bg-[#F9FAFB] py-8 px-4 sm:px-6 lg:px-8" style={{ fontFamily: "'Manrope', sans-serif" }} data-testid="my-checkout-payment-blocked">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-xl border-2 border-[#F59E0B] p-6 sm:p-8 text-center shadow-sm">
            <Warning size={40} weight="fill" className="text-[#F59E0B] mx-auto mb-3" />
            <h1 className="text-xl font-bold text-[#0F1829] mb-2">Falta um passo para concluir a compra</h1>
            <p className="text-[#4B5563] text-sm mb-6" data-testid="payment-block-msg">{block.msg}</p>
            <Link to={block.to} replace data-testid="payment-block-cta"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#0F1829] text-white rounded-md text-sm font-semibold hover:bg-[#002480] transition-colors">
              {block.cta}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-[#F9FAFB] py-8 px-4 sm:px-6 lg:px-8" style={{ fontFamily: "'Manrope', sans-serif" }} data-testid="my-checkout-payment-page">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          {/* Breadcrumb com 3 etapas — permite voltar ao carrinho ou às moradas */}
          <nav className="flex items-center gap-1.5 text-xs text-[#6B7280] mb-2 flex-wrap" data-testid="payment-breadcrumb">
            <Link to="/my/carrinho" className="hover:text-[#0F1829] transition-colors flex items-center gap-1"
              data-testid="payment-back-cart">
              <ArrowLeft size={12} /> Carrinho
            </Link>
            <span className="text-[#D1D5DB]">/</span>
            <Link to="/my/checkout" className="hover:text-[#0F1829] transition-colors"
              data-testid="payment-back-checkout">
              Dados de entrega
            </Link>
            <span className="text-[#D1D5DB]">/</span>
            <span className="text-[#0F1829] font-medium">Pagamento</span>
          </nav>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-[#0F1829] flex items-center gap-3" style={{ fontFamily: "'Fraunces', sans-serif" }}>
            <CreditCard size={30} weight="fill" className="text-[#002480]" />
            Pagamento
          </h1>
          <p className="text-[#6B7280] text-sm mt-1">Escolha o método de pagamento preferido. A encomenda é confirmada ao avançar.</p>
        </div>

        {loading ? (
          <div className="p-12 text-center text-[#6B7280] bg-white rounded-xl border border-[#E5E7EB]" data-testid="payment-loading">
            A carregar métodos de pagamento…
          </div>
        ) : methods.length === 0 ? (
          <div className="p-6 bg-white rounded-xl border border-[#E5E7EB] text-sm text-[#6B7280]" data-testid="payment-empty">
            Nenhum método de pagamento disponível. Por favor, contacte o apoio.
          </div>
        ) : (
          <div className="space-y-4">
            <PaymentMethodSelector methods={methods} selected={selected} onSelect={setSelected}
              total={displayTotal} currency={currency} billingNif={billingNif}
              mbwayPhone={mbwayPhone} onMbwayPhoneChange={setMbwayPhone}
              disabled={confirming} />

            <div className="bg-white rounded-xl border border-[#E5E7EB] p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.15em] text-[#6B7280]">Total a pagar</p>
                <p className="text-2xl font-bold text-[#0F1829] mt-1" data-testid="payment-total">
                  {fmt(displayTotal, currency)}
                </p>
                {(acceptedVouchers > 0 || acceptedCredits > 0 || acceptedPromoKey) && (
                  <div className="mt-1 space-y-0.5 text-[11px] text-[#6B7280]" data-testid="payment-discounts-summary">
                    {acceptedPromoKey && <p>Código promocional aplicado (<span className="font-mono">{acceptedPromoKey}</span>)</p>}
                    {acceptedVouchers > 0 && <p>Vales usados: <span className="font-semibold text-[#0F1829]">{fmt(acceptedVouchers, currency)}</span></p>}
                    {acceptedCredits > 0 && <p>Créditos usados: <span className="font-semibold text-[#0F1829]">{fmt(acceptedCredits, currency)}</span></p>}
                  </div>
                )}
              </div>
              <button type="button" onClick={confirm} disabled={confirming}
                data-testid="payment-confirm-btn"
                className="w-full sm:w-auto px-6 py-3 bg-[#0F1829] text-white rounded-md text-sm font-semibold hover:bg-[#002480] disabled:opacity-50 disabled:cursor-not-allowed transition-colors active:scale-[0.98] flex items-center gap-2 justify-center">
                {confirming ? <SpinnerGap size={16} className="animate-spin" /> : null}
                Confirmar e pagar
              </button>
            </div>

            {confirmError && (
              <div data-testid="payment-confirm-error" role="alert" aria-live="assertive"
                className="p-3 rounded-md bg-red-50 border-2 border-[#DC2626] text-[#DC2626] text-sm font-bold flex items-start gap-2 shadow-sm">
                <Warning size={18} className="mt-0.5 shrink-0" />
                <span>{confirmError}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
